import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search, X, LayoutDashboard, FileText, FilePlus, FileStack,
    Users, Sparkles, BarChart3, TrendingUp, Settings, User,
    Briefcase, DollarSign, Bell, Shield, UserCog, ArrowRight,
    Building2, Wrench, Hash, FileEdit
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { cotizacionesAPI } from '../services/api';
import { IconBox, Kbd } from './ui';

const formatMonto = (val) => {
    const num = parseFloat(val);
    if (!isFinite(num)) return null;
    return `S/ ${num.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * CommandPalette — overlay con buscador estilo Ctrl+K.
 *
 * Atajos:
 *   - Ctrl+K (Cmd+K en Mac): abrir
 *   - Esc: cerrar
 *   - ↑↓: navegar
 *   - Enter: seleccionar
 *
 * Acciones:
 *   - Navegar a paginas (filtradas por permisos)
 *   - (futuro) Buscar cotizaciones / clientes recientes
 */

const buildActions = (tienePermiso) => {
    const acts = [];

    // Grupo: Páginas principales
    acts.push({ group: 'Inicio',     id: 'dashboard',   label: 'Dashboard',          to: '/dashboard',                icon: LayoutDashboard, color: 'blue'  });

    if (tienePermiso('usar_ia')) {
        acts.push({ group: 'Inteligencia Artificial', id: 'ai-gen',  label: 'Cotizar con IA',    to: '/cotizaciones/generar-ia', icon: Sparkles,  color: 'purple' });
    }
    if (tienePermiso('usar_ia') || tienePermiso('ver_metricas_ia')) {
        acts.push({ group: 'Inteligencia Artificial', id: 'ai-ana',  label: 'Análisis Dashboard', to: '/dashboard#analisis-ia',  icon: BarChart3, color: 'purple' });
    }
    if (tienePermiso('ver_metricas_ia')) {
        acts.push({ group: 'Inteligencia Artificial', id: 'metricas', label: 'Métricas IA',       to: '/metricas-ia',             icon: TrendingUp, color: 'purple' });
    }

    if (tienePermiso('gestion_cotizaciones')) {
        acts.push({ group: 'Cotizaciones', id: 'cot-nueva',        label: 'Nueva cotización (Normal)',  to: '/cotizaciones/nueva',        icon: FilePlus,  color: 'cyan' });
        acts.push({ group: 'Cotizaciones', id: 'cot-nueva-manual', label: 'Nueva cotización (Histórico)', to: '/cotizaciones/nueva/manual', icon: FileEdit,  color: 'cyan' });
        acts.push({ group: 'Cotizaciones', id: 'cot-listado',  label: 'Listado',           to: '/cotizaciones',                icon: FileText,  color: 'cyan' });
        acts.push({ group: 'Cotizaciones', id: 'cot-clientes', label: 'Por cliente',       to: '/cotizaciones/clientes',       icon: Users,     color: 'cyan' });
    }
    acts.push({ group: 'Cotizaciones', id: 'cot-mis', label: 'Mis cotizaciones',          to: '/cotizaciones/mis-cotizaciones', icon: FileText,  color: 'amber' });
    if (tienePermiso('gestion_plantillas')) {
        acts.push({ group: 'Cotizaciones', id: 'plantillas',   label: 'Plantillas',        to: '/plantillas',                  icon: FileStack, color: 'cyan' });
    }

    if (tienePermiso('gestion_clientes')) {
        acts.push({ group: 'Clientes', id: 'clientes', label: 'Gestionar clientes',  to: '/clientes', icon: Users, color: 'green' });
    }

    // Catálogos — datos maestros que alimentan el form de cotización
    if (tienePermiso('gestion_servicios'))      acts.push({ group: 'Catálogos', id: 'servicios',  label: 'Servicios',     to: '/servicios',    icon: Briefcase,  color: 'neutral' });
    if (tienePermiso('gestion_personal'))       acts.push({ group: 'Catálogos', id: 'personal',   label: 'Personal',      to: '/personal',     icon: Users,      color: 'neutral' });
    if (tienePermiso('gestion_costos_fijos'))   acts.push({ group: 'Catálogos', id: 'costos',     label: 'Costos fijos',  to: '/costos-fijos', icon: DollarSign, color: 'neutral' });

    // Ajustes — mismo orden y items que el dropdown "Ajustes" del sidebar
    if (tienePermiso('configuracion'))          acts.push({ group: 'Ajustes', id: 'config',         label: 'Configuración',     to: '/configuracion',     icon: Wrench,    color: 'neutral' });
    if (tienePermiso('configuracion'))          acts.push({ group: 'Ajustes', id: 'datos-empresa',  label: 'Datos de empresa',  to: '/datos-empresa',     icon: Building2, color: 'neutral' });
    if (tienePermiso('configuracion'))          acts.push({ group: 'Ajustes', id: 'numeraciones',   label: 'Numeraciones',      to: '/numeraciones',      icon: Hash,      color: 'neutral' });
    if (tienePermiso('configuracion'))          acts.push({ group: 'Ajustes', id: 'config-ia',      label: 'Proveedores IA',    to: '/configuracion-ia',  icon: Sparkles,  color: 'neutral' });
    if (tienePermiso('configuracion_alertas'))  acts.push({ group: 'Ajustes', id: 'alertas',        label: 'Alertas',           to: '/alertas',           icon: Bell,      color: 'neutral' });

    // Acceso — usuarios y roles
    if (tienePermiso('gestion_usuarios'))       acts.push({ group: 'Acceso', id: 'usuarios', label: 'Usuarios',           to: '/usuarios', icon: UserCog, color: 'neutral' });
    if (tienePermiso('gestion_roles'))          acts.push({ group: 'Acceso', id: 'roles',    label: 'Roles y permisos',   to: '/roles',    icon: Shield,  color: 'neutral' });

    // Cuenta personal
    acts.push({ group: 'Cuenta', id: 'notificaciones', label: 'Notificaciones', to: '/notificaciones', icon: Bell, color: 'neutral' });
    acts.push({ group: 'Cuenta', id: 'perfil',         label: 'Mi perfil',      to: '/perfil',         icon: User, color: 'neutral' });

    return acts;
};

const CommandPalette = () => {
    const navigate = useNavigate();
    const { tienePermiso } = useAuth();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIdx, setActiveIdx] = useState(0);
    const [cotizaciones, setCotizaciones] = useState([]);
    const [cotizacionesLoaded, setCotizacionesLoaded] = useState(false);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    const puedeBuscarCotizaciones =
        tienePermiso('gestion_cotizaciones') || tienePermiso('cotizaciones_asignadas');

    const allActions = useMemo(() => buildActions(tienePermiso), [tienePermiso]);

    // Lazy load de cotizaciones la primera vez que se abre el palette
    useEffect(() => {
        if (!open || cotizacionesLoaded || !puedeBuscarCotizaciones) return;
        let cancelled = false;
        const cargar = async () => {
            try {
                const fetcher = tienePermiso('gestion_cotizaciones')
                    ? cotizacionesAPI.getAll({})
                    : cotizacionesAPI.getMyCotizaciones();
                const resp = await fetcher;
                if (cancelled) return;
                setCotizaciones(resp.data || []);
                setCotizacionesLoaded(true);
            } catch {
                if (!cancelled) setCotizacionesLoaded(true);
            }
        };
        cargar();
        return () => { cancelled = true; };
    }, [open, cotizacionesLoaded, puedeBuscarCotizaciones, tienePermiso]);

    // Filtrar acciones del menu por query
    const filteredMenu = useMemo(() => {
        if (!query.trim()) return allActions;
        const q = query.toLowerCase();
        return allActions.filter(a =>
            a.label.toLowerCase().includes(q) ||
            a.group.toLowerCase().includes(q)
        );
    }, [allActions, query]);

    // Filtrar cotizaciones por query (numero, cliente, empresa)
    const filteredCotizaciones = useMemo(() => {
        if (!query.trim() || !puedeBuscarCotizaciones) return [];
        const q = query.toLowerCase().trim();
        const matches = cotizaciones.filter(c => {
            const numero = String(c.numero || '').toLowerCase();
            const cliente = String(c.cliente_nombre || '').toLowerCase();
            const empresa = String(c.cliente_empresa || '').toLowerCase();
            return numero.includes(q) || cliente.includes(q) || empresa.includes(q);
        });
        return matches.slice(0, 6);
    }, [query, cotizaciones, puedeBuscarCotizaciones]);

    // Combinar: menu items + cotizaciones encontradas. Todos comparten un indice secuencial
    // para que las flechas naveguen toda la lista (no solo el menu).
    const filtered = useMemo(() => {
        const cotItems = filteredCotizaciones.map(c => ({
            group: 'Cotizaciones encontradas',
            id: `cot-${c.id}`,
            label: c.numero,
            sublabel: [c.cliente_nombre, c.cliente_empresa].filter(Boolean).join(' • '),
            extra: formatMonto(c.total),
            to: `/cotizaciones/${c.id}`,
            icon: FileText,
            color: 'cyan',
            isCotizacion: true,
        }));
        // Cotizaciones primero (mas relevante cuando el user escribe COT-XXX o un cliente)
        return [...cotItems, ...filteredMenu];
    }, [filteredCotizaciones, filteredMenu]);

    // Atajos globales: Ctrl+K / Cmd+K para abrir, Esc para cerrar
    useEffect(() => {
        const handler = (e) => {
            const isCmdK = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k';
            if (isCmdK) {
                e.preventDefault();
                setOpen(v => !v);
                return;
            }
            if (e.key === 'Escape' && open) {
                e.preventDefault();
                setOpen(false);
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open]);

    // Resetear estado al abrir / cerrar
    useEffect(() => {
        if (open) {
            setQuery('');
            setActiveIdx(0);
            // Esperar al render del input para enfocar
            setTimeout(() => inputRef.current?.focus(), 0);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [open]);

    // Si query cambia y activeIdx queda fuera de rango, resetear
    useEffect(() => {
        if (activeIdx >= filtered.length) setActiveIdx(0);
    }, [filtered.length, activeIdx]);

    // Auto-scroll del item activo a la vista
    useEffect(() => {
        if (!open || !listRef.current) return;
        const el = listRef.current.querySelector(`[data-idx="${activeIdx}"]`);
        if (el) el.scrollIntoView({ block: 'nearest' });
    }, [activeIdx, open]);

    const handleAction = (action) => {
        setOpen(false);
        // Hash anchors (ej /dashboard#analisis-ia)
        if (action.to.includes('#')) {
            const [path, hash] = action.to.split('#');
            navigate(path);
            setTimeout(() => {
                const target = document.getElementById(hash);
                if (target) target.scrollIntoView({ behavior: 'smooth' });
            }, 120);
        } else {
            navigate(action.to);
        }
    };

    const handleKey = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIdx(i => (i + 1) % Math.max(1, filtered.length));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIdx(i => (i - 1 + filtered.length) % Math.max(1, filtered.length));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const act = filtered[activeIdx];
            if (act) handleAction(act);
        }
    };

    if (!open) return null;

    // Agrupar visualmente por `group`
    const grouped = filtered.reduce((acc, act, idx) => {
        const last = acc[acc.length - 1];
        if (last && last.group === act.group) {
            last.items.push({ ...act, _idx: idx });
        } else {
            acc.push({ group: act.group, items: [{ ...act, _idx: idx }] });
        }
        return acc;
    }, []);

    return (
        <div
            className="fixed inset-0 z-[2000] flex items-start justify-center p-4 pt-[10vh] sm:pt-[14vh]"
            role="dialog"
            aria-modal="true"
        >
            {/* Overlay clickable: cierra al hacer click fuera */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-[fade_120ms_ease-out_forwards]"
                onClick={() => setOpen(false)}
            />

            {/* Caja: stopPropagation para que clicks dentro NO cierren */}
            <div
                className="relative w-full max-w-2xl bg-surface border border-line rounded-2xl shadow-soft-lg overflow-hidden animate-[fadeScale_140ms_ease-out_forwards]"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Input */}
                <div className="flex items-center gap-3 px-4 h-14 border-b border-line">
                    <Search size={18} className="text-muted shrink-0" />
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder={puedeBuscarCotizaciones
                            ? "Busca menú, número (COT, MPW, MANUAL…), cliente o empresa…"
                            : "¿Qué quieres abrir?"}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={handleKey}
                        className="
                            flex-1 bg-transparent outline-none text-sm
                            text-text placeholder:text-muted
                        "
                    />
                    <button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="
                            inline-flex items-center gap-1 px-2 py-1 rounded-md
                            text-[10px] font-mono font-medium uppercase tracking-wide text-muted
                            bg-surface-2 border border-line
                            hover:text-text transition
                        "
                        aria-label="Cerrar"
                    >
                        Esc
                    </button>
                </div>

                {/* Lista */}
                <div ref={listRef} className="max-h-[60vh] overflow-y-auto p-2">
                    {grouped.length === 0 ? (
                        <div className="py-10 text-center text-sm text-muted">
                            Sin resultados para "{query}"
                        </div>
                    ) : (
                        grouped.map((g, gi) => (
                            <div key={gi} className="mb-2 last:mb-0">
                                <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted">
                                    {g.group}
                                </div>
                                {g.items.map((act) => {
                                    const Icon = act.icon;
                                    const isActive = act._idx === activeIdx;
                                    return (
                                        <button
                                            key={act.id}
                                            type="button"
                                            data-idx={act._idx}
                                            onMouseEnter={() => setActiveIdx(act._idx)}
                                            onClick={() => handleAction(act)}
                                            className={`
                                                w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg
                                                text-left transition
                                                ${isActive ? 'bg-surface-2' : 'hover:bg-surface-2/60'}
                                            `}
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                <IconBox color={act.color} size="sm">
                                                    <Icon size={14} />
                                                </IconBox>
                                                <div className="min-w-0 flex flex-col">
                                                    <span className="text-sm text-text truncate">{act.label}</span>
                                                    {act.sublabel && (
                                                        <span className="text-[11px] text-muted truncate">{act.sublabel}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                {act.extra && (
                                                    <span className="text-[11px] font-medium text-muted">{act.extra}</span>
                                                )}
                                                <ArrowRight size={14} className={`transition ${isActive ? 'text-primary' : 'text-muted'}`} />
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        ))
                    )}
                </div>

                {/* Footer hints */}
                <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-t border-line bg-surface-2/50 text-[11px] text-muted">
                    <div className="flex items-center gap-3">
                        <span className="inline-flex items-center gap-1">
                            <Kbd>↑</Kbd><Kbd>↓</Kbd> navegar
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <Kbd>↵</Kbd> abrir
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <Kbd>Esc</Kbd> cerrar
                        </span>
                    </div>
                    <span className="hidden sm:inline-flex items-center gap-1">
                        <Kbd>Ctrl</Kbd>+<Kbd>K</Kbd> abre el buscador
                    </span>
                </div>
            </div>

            <style>{`
                @keyframes fade { from { opacity: 0; } to { opacity: 1; } }
                @keyframes fadeScale {
                    from { opacity: 0; transform: scale(0.96) translateY(-4px); }
                    to   { opacity: 1; transform: scale(1) translateY(0); }
                }
            `}</style>
        </div>
    );
};

export default CommandPalette;
